import config from "../lib/config";

const ACTIVE_ACCOUNT_KEY = "crypto_recent_play_active_account";
const STORAGE_PREFIX = "crypto_recent_play_history";
const MAX_RECENT_ITEMS = 12;

const trimLeadingSlash = (value = "") => value.replace(/^\/+/, "");

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `${config.backendUrl}/${trimLeadingSlash(trimmed)}`;
};

const uniqueUrls = (urls) => Array.from(new Set(urls.filter(Boolean)));

const normalizeText = (value = "") =>
  String(value || "")
    .replace(/\bBTI\b/gi, "비티아이")
    .replace(/\bBT1\b/gi, "비티원")
    .replace(/\bSABA\b/gi, "사바")
    .replace(/\bSA(?=게이밍|카지노|스포츠|\s|$)/gi, "에스에이")
    .replace(/\s+/g, " ")
    .trim();

const getProfile = (user) => user?.profile || user?.user || user?.member || user || {};

const parseMaybeJson = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return [value];
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object") return [parsed];
  } catch {
    // Some older admin fields are plain text or comma-separated strings.
  }

  return trimmed
    .split(/[,|\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const getRecentPlayAccountKey = (user) => {
  if (!user?.isAuthenticated && !user?.profile) return "";

  const profile = getProfile(user);
  return String(
    user?.id ||
      profile?.id ||
      profile?.user_id ||
      profile?.member_id ||
      profile?.user_unique_id ||
      user?.username ||
      profile?.username ||
      profile?.nickname ||
      ""
  ).trim();
};

const getStorageKey = (accountKey) => `${STORAGE_PREFIX}:${accountKey}`;

export const setRecentPlayAccountKey = (user) => {
  if (typeof window === "undefined") return "";
  const accountKey = getRecentPlayAccountKey(user);

  try {
    if (accountKey) {
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountKey);
    } else {
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
    }
  } catch {
    // Recent play is a non-critical local enhancement.
  }

  return accountKey;
};

const getActiveAccountKey = () => {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem(ACTIVE_ACCOUNT_KEY) || "";
  } catch {
    return "";
  }
};

const readRows = (accountKey) => {
  if (typeof window === "undefined" || !accountKey) return [];

  try {
    const raw = localStorage.getItem(getStorageKey(accountKey));
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRows = (accountKey, rows) => {
  if (typeof window === "undefined" || !accountKey) return;

  try {
    localStorage.setItem(getStorageKey(accountKey), JSON.stringify(rows.slice(0, MAX_RECENT_ITEMS)));
  } catch {
    // Ignore storage failures; launch flow must not be blocked by this feature.
  }
};

const getVendorImage = (vendor) =>
  uniqueUrls([
    resolveAssetUrl(vendor?.image),
    resolveAssetUrl(vendor?.pc_image),
    resolveAssetUrl(vendor?.mobile_image),
    resolveAssetUrl(vendor?.thumbnail),
    resolveAssetUrl(vendor?.vendor_list?.thumbnails),
  ])[0] || "";

const getRecentImage = (row) =>
  uniqueUrls([
    resolveAssetUrl(row?.image),
    resolveAssetUrl(row?.game_image),
    resolveAssetUrl(row?.thumbnail),
    resolveAssetUrl(row?.thumbnail_url),
    resolveAssetUrl(row?.pc_image),
    resolveAssetUrl(row?.mobile_image),
    resolveAssetUrl(row?.vendor_list?.thumbnails),
  ])[0] || "";

const getRecentTitle = (row, fallback = "게임") =>
  normalizeText(
    row?.title ||
      row?.game_name_ko ||
      row?.game_name_kr ||
      row?.name_ko ||
      row?.display_name ||
      row?.game_name ||
      row?.name ||
      row?.vendor_name_kr ||
      row?.vendor_name_ko ||
      row?.vendor_name ||
      fallback
  );

const getRecentMeta = (row, fallback = "최근 실행") =>
  normalizeText(
    row?.meta ||
      row?.subtitle ||
      row?.vendor_name_kr ||
      row?.vendor_name_ko ||
      row?.vendor ||
      row?.vendor_name ||
      row?.game_type ||
      row?.gameType ||
      fallback
  );

export const createRecentProviderItem = (vendor = {}, options = {}) => {
  const lobbyId = vendor?.vendor_list?.lobby_id || vendor?.lobby_id || options.lobbyId || "";
  const vendorName = vendor?.vendor_name || options.vendorName || "";
  const title = normalizeText(
    options.label ||
      vendor?.vendor_name_kr ||
      vendor?.vendor_name_ko ||
      vendor?.display_name ||
      vendorName ||
      "게임"
  );

  return {
    key: `provider:${lobbyId || vendorName || title}`,
    kind: "provider",
    gameType: options.gameType || vendor?.game_type || vendor?.type || "",
    lobbyId,
    vendorName,
    title,
    image: options.image || getVendorImage(vendor),
    meta: options.meta || "최근 실행",
  };
};

export const createRecentGameItem = (gameId, options = {}) => {
  const recent = options.recentPlay || {};
  const title = normalizeText(options.label || recent.title || "게임");
  const vendorName = recent.vendorName || recent.vendor || "";

  return {
    key: recent.key || `game:${gameId || recent.gameId || title}`,
    kind: recent.kind || "slot-game",
    gameType: recent.gameType || "slots",
    gameId: gameId || recent.gameId || "",
    vendorName,
    lobbyId: recent.lobbyId || "",
    title,
    image: recent.image || options.image || "",
    meta: recent.meta || recent.subtitle || "최근 실행",
  };
};

export const normalizeRecentPlayRows = (sourceRows = [], fallbackMeta = "최근 실행") => {
  const rows = parseMaybeJson(sourceRows);
  const seen = new Set();

  return rows
    .map((row, index) => {
      if (typeof row === "string") {
        const title = normalizeText(row);
        if (!title) return null;
        return {
          key: `profile-text:${title}`,
          kind: "unknown",
          title,
          image: "",
          meta: fallbackMeta,
          disabled: true,
          updatedAt: Date.now() - index,
        };
      }

      const gameId =
        row?.gameId ||
        row?.game_id ||
        row?.provider_game_id ||
        row?.providerGameId ||
        row?.game?.game_id ||
        row?.game?.id ||
        row?.id ||
        "";
      const lobbyId =
        row?.lobbyId ||
        row?.lobby_id ||
        row?.vendor_lobby_id ||
        row?.vendor?.lobby_id ||
        row?.vendor_list?.lobby_id ||
        "";
      const vendorName =
        row?.vendorName ||
        row?.vendor ||
        row?.vendor_name ||
        row?.provider ||
        row?.vendor?.vendor_name ||
        "";
      const gameType = row?.gameType || row?.game_type || row?.type || "";
      const title = getRecentTitle(row, vendorName || "게임");
      const isProvider = Boolean(lobbyId) && !gameId;
      const key =
        row?.key ||
        (isProvider
          ? `provider:${lobbyId || vendorName || title}`
          : `game:${gameId || `${vendorName}:${title}`}`);

      if (!title || seen.has(key)) return null;
      seen.add(key);

      return {
        key,
        kind: row?.kind || (isProvider ? "provider" : "slot-game"),
        gameType,
        gameId,
        lobbyId,
        vendorName,
        title,
        image: getRecentImage(row),
        meta: getRecentMeta(row, fallbackMeta),
        disabled: !gameId && !lobbyId,
        updatedAt: row?.updatedAt || row?.created_at || row?.createdAt || row?.date || Date.now() - index,
      };
    })
    .filter(Boolean);
};

const collectProfileRecentSources = (user) => {
  const profile = getProfile(user);
  const userInfo = profile?.user_info || profile?.userInfo || {};

  return [
    profile?.recent_games,
    profile?.recentGames,
    profile?.recent_game_list,
    profile?.recentGameList,
    userInfo?.recent_games,
    userInfo?.recentGames,
    userInfo?.recent_game_list,
    userInfo?.recentGameList,
  ];
};

export const getProfileRecentPlayHistory = (user) =>
  collectProfileRecentSources(user).flatMap((source) =>
    normalizeRecentPlayRows(source, "프로필 기록")
  );

export const mergeRecentPlayRows = (...groups) => {
  const seen = new Set();
  return groups
    .flat()
    .filter(Boolean)
    .sort((a, b) => Number(b?.updatedAt || 0) - Number(a?.updatedAt || 0))
    .filter((row) => {
      if (!row?.key || seen.has(row.key)) return false;
      seen.add(row.key);
      return true;
    })
    .slice(0, MAX_RECENT_ITEMS);
};

export const recordRecentPlayItem = (item) => {
  if (typeof window === "undefined" || !item?.key || !item?.title) return [];

  const accountKey = getActiveAccountKey();
  if (!accountKey) return [];

  const normalized = {
    ...item,
    title: normalizeText(item.title),
    meta: normalizeText(item.meta || "최근 실행"),
    image: item.image || "",
    updatedAt: Date.now(),
  };
  const rows = readRows(accountKey).filter((row) => row?.key !== normalized.key);
  const nextRows = [normalized, ...rows].slice(0, MAX_RECENT_ITEMS);
  writeRows(accountKey, nextRows);

  try {
    window.dispatchEvent(
      new CustomEvent("crypto-recent-play-updated", {
        detail: { accountKey, rows: nextRows },
      })
    );
  } catch {
    // Event dispatch is only for UI refresh.
  }

  return nextRows;
};

export const getRecentPlayHistory = (userOrAccountKey) => {
  const accountKey =
    typeof userOrAccountKey === "string"
      ? userOrAccountKey
      : getRecentPlayAccountKey(userOrAccountKey);
  return readRows(accountKey);
};
